import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ACCENT = '#60A5FA';

const emptyDebt = (direction = 'receivable') => ({
  person: '',
  direction,
  amount: '',
  remaining: '',
  reason: '',
  notes: '',
  due_date: null,
  status: 'open',
  company: '',
});

const toDateKey = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseDateKey = (key) => {
  if (!key) return null;
  const [y, m, d] = String(key).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

const DebtModal = ({ show, onHide, onSubmit, initialData = null, companies = [] }) => {
  const [form, setForm] = useState(emptyDebt());

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      setForm({
        person: initialData.person || '',
        direction: initialData.direction || 'receivable',
        amount: String(initialData.amount ?? ''),
        remaining: String(initialData.remaining ?? initialData.amount ?? ''),
        reason: initialData.reason || '',
        notes: initialData.notes || '',
        due_date: parseDateKey(initialData.due_date),
        status: initialData.status || 'open',
        company: initialData.company ? String(initialData.company) : '',
      });
    } else {
      setForm(emptyDebt());
    }
  }, [show, initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = Math.abs(parseFloat(form.amount) || 0);
    const remainingRaw = form.remaining === '' ? amount : Math.abs(parseFloat(form.remaining) || 0);
    onSubmit({
      person: form.person.trim(),
      direction: form.direction,
      amount: amount.toFixed(2),
      remaining: remainingRaw.toFixed(2),
      reason: form.reason.trim(),
      notes: form.notes.trim(),
      due_date: form.due_date ? toDateKey(form.due_date) : null,
      status: form.status,
      company: form.company ? parseInt(form.company, 10) : null,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" style={{ borderBottom: '1px solid #333' }}>
        <Modal.Title>{initialData ? 'Edit debt' : 'Who owes money'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Direction</Form.Label>
            <div className="d-flex gap-2">
              <Button
                type="button"
                variant={form.direction === 'receivable' ? 'success' : 'outline-success'}
                className="flex-grow-1"
                onClick={() => setForm((p) => ({ ...p, direction: 'receivable' }))}
              >
                They owe me
              </Button>
              <Button
                type="button"
                variant={form.direction === 'payable' ? 'danger' : 'outline-danger'}
                className="flex-grow-1"
                onClick={() => setForm((p) => ({ ...p, direction: 'payable' }))}
              >
                I owe them
              </Button>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Person</Form.Label>
            <Form.Control
              required
              value={form.person}
              onChange={(e) => setForm({ ...form, person: e.target.value })}
              placeholder="Name"
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Reason</Form.Label>
            <Form.Control
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Lent for rent, unpaid invoice, shared trip"
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Original amount (€)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={form.amount}
                  onChange={(e) => {
                    const amount = e.target.value;
                    setForm((p) => ({
                      ...p,
                      amount,
                      remaining: initialData ? p.remaining : amount,
                    }));
                  }}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Still owed (€)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.remaining}
                  onChange={(e) => setForm({ ...form, remaining: e.target.value })}
                  className="bg-dark text-light border-secondary"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ display: 'block' }}>Due date</Form.Label>
                <DatePicker
                  selected={form.due_date}
                  onChange={(date) => setForm({ ...form, due_date: date })}
                  className="form-control bg-dark text-light border-secondary"
                  dateFormat="dd/MM/yyyy"
                  isClearable
                  placeholderText="Optional"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  <option value="open">Open</option>
                  <option value="partial">Partially paid</option>
                  <option value="settled">Settled</option>
                  <option value="written_off">Written off</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {companies.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Company (optional)</Form.Label>
              <Form.Select
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="bg-dark text-light border-secondary"
              >
                <option value="">None</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-1">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-dark text-light border-secondary"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #333' }}>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" style={{ backgroundColor: ACCENT, borderColor: ACCENT }}>
            {initialData ? 'Save' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default DebtModal;
